import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../context/AuthContext';
import { whatsappService } from '../../services/api';
import toast from 'react-hot-toast';
import { Spinner } from '../../components/Spinner';
import { ArrowPathIcon, LinkIcon, PhoneIcon, TrashIcon } from '@heroicons/react/24/outline';

const errorOf = (e, fallback) => e.response?.data?.message || fallback;

const LANGUAGES = [
  ['en', 'English'], ['en_US', 'English (US)'], ['en_GB', 'English (UK)'], ['hi', 'Hindi'], ['mr', 'Marathi'],
  ['gu', 'Gujarati'], ['pa', 'Punjabi'], ['ta', 'Tamil'], ['te', 'Telugu'], ['kn', 'Kannada'], ['ml', 'Malayalam'], ['bn', 'Bengali'],
];

const STATUS_BADGE = {
  APPROVED: 'badge-success',
  PENDING:  'badge-warning',
  IN_APPEAL: 'badge-warning',
  REJECTED: 'badge-danger',
  PAUSED:   'badge-secondary',
  DISABLED: 'badge-secondary',
};

// Highest {{n}} used, so the form shows one sample box per variable
const variableCount = (text) => {
  const nums = [...String(text || '').matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map(m => Number(m[1]));
  return nums.length ? Math.max(...nums) : 0;
};

const fill = (text, samples) =>
  String(text || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (all, n) => samples[Number(n) - 1] || all);

// How the message will look on the customer's phone
const Preview = ({ form }) => {
  const header = fill(form.headerText, [form.headerExample]);
  const body   = fill(form.body, form.bodyExamples);
  const buttons = [
    ...form.quickReplies.filter(Boolean).map(t => ({ text: t })),
    ...(form.urlButtonText ? [{ text: form.urlButtonText, icon: LinkIcon }] : []),
    ...(form.phoneButtonText ? [{ text: form.phoneButtonText, icon: PhoneIcon }] : []),
  ];
  return (
    <div className="rounded-xl p-4" style={{ background: '#e5ddd5' }}>
      <div className="bg-white rounded-lg shadow-sm max-w-xs overflow-hidden">
        <div className="px-3 py-2 space-y-1">
          {header && <p className="text-sm font-semibold text-gray-900">{header}</p>}
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{body || <span className="text-gray-400">Your message…</span>}</p>
          {form.footer && <p className="text-xs text-gray-400">{form.footer}</p>}
        </div>
        {buttons.map((b, i) => (
          <div key={i} className="border-t border-gray-100 py-2 text-center text-sm text-sky-600 flex items-center justify-center gap-1">
            {b.icon && <b.icon className="h-4 w-4" />}{b.text}
          </div>
        ))}
      </div>
    </div>
  );
};

const EMPTY = {
  name: '', category: 'UTILITY', language: 'en',
  headerText: '', headerExample: '', body: '', bodyExamples: [], footer: '',
  quickReplies: ['', '', ''], urlButtonText: '', urlButtonUrl: '', phoneButtonText: '', phoneButtonNumber: '',
};

const NewTemplateForm = ({ numberId, onDone }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const bodyVars = variableCount(form.body);
  const headerHasVar = variableCount(form.headerText) > 0;

  const create = useMutation(() => whatsappService.createMessageTemplate(numberId, form), {
    onSuccess: () => {
      toast.success('Template sent to Meta for review');
      queryClient.invalidateQueries(['whatsappMessageTemplates', numberId]);
      onDone();
    },
    onError: (e) => toast.error(errorOf(e, 'Failed to create template'), { duration: 8000 }),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); create.mutate(); }} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">New template</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                className="input text-sm w-full" placeholder="e.g. test_drive_followup" required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-500 mb-1">Category</label>
              <select value={form.category} onChange={set('category')} className="input text-sm w-full">
                <option value="UTILITY">Utility — updates about an enquiry, booking or test drive</option>
                <option value="MARKETING">Marketing — offers, promotions, new launches</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Language</label>
              <select value={form.language} onChange={set('language')} className="input text-sm w-full">
                {LANGUAGES.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Header (optional)</label>
            <input value={form.headerText} onChange={set('headerText')} className="input text-sm w-full" maxLength={60} placeholder="e.g. Your test drive is booked" />
            {headerHasVar && (
              <input value={form.headerExample} onChange={set('headerExample')} className="input text-sm w-full mt-2" placeholder="Sample for {{1}}" />
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
            <textarea
              value={form.body} onChange={set('body')} rows={5} maxLength={1024} required
              className="input text-sm w-full"
              placeholder={'Hi {{1}}, thanks for your interest in the {{2}}. When would you like a test drive?'}
            />
            <p className="text-xs text-gray-400 mt-1">Use {'{{1}}'}, {'{{2}}'}… for details that change per customer, like their name or the car.</p>
            {bodyVars > 0 && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                {Array.from({ length: bodyVars }).map((_, i) => (
                  <input
                    key={i}
                    value={form.bodyExamples[i] || ''}
                    onChange={e => setForm(f => { const next = [...f.bodyExamples]; next[i] = e.target.value; return { ...f, bodyExamples: next }; })}
                    className="input text-sm" placeholder={`Sample for {{${i + 1}}}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Footer (optional)</label>
            <input value={form.footer} onChange={set('footer')} className="input text-sm w-full" maxLength={60} placeholder="e.g. Reply STOP to opt out" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Quick reply buttons (optional)</label>
            <div className="grid grid-cols-3 gap-2">
              {form.quickReplies.map((q, i) => (
                <input
                  key={i} value={q} maxLength={25} placeholder={`Button ${i + 1}`} className="input text-sm"
                  onChange={e => setForm(f => { const next = [...f.quickReplies]; next[i] = e.target.value; return { ...f, quickReplies: next }; })}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Website button (optional)</label>
              <input value={form.urlButtonText} onChange={set('urlButtonText')} maxLength={25} className="input text-sm w-full" placeholder="Button text" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">&nbsp;</label>
              <input value={form.urlButtonUrl} onChange={set('urlButtonUrl')} className="input text-sm w-full" placeholder="https://…" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Call button (optional)</label>
              <input value={form.phoneButtonText} onChange={set('phoneButtonText')} maxLength={25} className="input text-sm w-full" placeholder="Button text" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">&nbsp;</label>
              <input value={form.phoneButtonNumber} onChange={set('phoneButtonNumber')} className="input text-sm w-full" placeholder="+919876543210" />
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-500">Preview</p>
          <Preview form={form} />
          <p className="text-xs text-gray-400">
            Meta reviews every template, usually within minutes, sometimes up to a day. Only approved templates can be sent.
          </p>
        </div>
      </div>
      <div className="flex gap-2 mt-6">
        <button type="submit" disabled={create.isLoading} className="btn-primary text-sm">
          {create.isLoading ? <Spinner size="sm" /> : 'Submit for review'}
        </button>
        <button type="button" onClick={onDone} className="btn-secondary text-sm">Cancel</button>
      </div>
    </form>
  );
};

const bodyOf = (t) => (t.components || []).find(c => c.type === 'BODY')?.text || '';

export const WhatsAppTemplates = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = ['super_admin', 'dealer'].includes(user?.role);
  const { data: numData, isLoading: numLoading } = useQuery('whatsappNumbers', whatsappService.getNumbers, { enabled: canManage, retry: false });
  const numbers = numData?.data?.data?.numbers || [];
  const dealers = numData?.data?.data?.dealers || [];
  const [chosenId, setNumberId] = useState('');
  const [creating, setCreating] = useState(false);
  const numberId = chosenId || numbers[0]?._id || '';

  const { data: tplData, isLoading: tplLoading, isFetching, refetch } = useQuery(
    ['whatsappMessageTemplates', numberId],
    () => whatsappService.listMessageTemplates(numberId),
    { enabled: !!numberId, retry: false, onError: (e) => toast.error(errorOf(e, 'Could not load templates from Meta')) }
  );
  const templates = tplData?.data?.data?.templates || [];

  const remove = useMutation((name) => whatsappService.deleteMessageTemplate(numberId, name), {
    onSuccess: () => { toast.success('Template deleted'); queryClient.invalidateQueries(['whatsappMessageTemplates', numberId]); },
    onError:   (e) => toast.error(errorOf(e, 'Failed to delete template')),
  });

  if (!canManage) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
        WhatsApp templates are managed by the Super Admin and each dealer.
      </div>
    );
  }
  if (numLoading) return <div className="p-6 text-center"><Spinner size="md" /></div>;

  const dealerName = (id) => {
    const d = dealers.find(x => String(x._id) === String(id));
    return d ? `${d.firstName || ''} ${d.lastName || ''}`.trim() : 'Workspace';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            The first message to a customer, and any message after 24 hours of silence, must be an approved template.
          </p>
        </div>
        {numbers.length > 0 && (
          <div className="flex items-center gap-2">
            <select value={numberId} onChange={e => { setNumberId(e.target.value); setCreating(false); }} className="input text-sm">
              {numbers.map(n => (
                <option key={n._id} value={n._id}>
                  {n.displayPhoneNumber || n.phoneNumberId}{user.role === 'super_admin' ? ` — ${dealerName(n.dealerId)}` : ''}{n.label ? ` (${n.label})` : ''}
                </option>
              ))}
            </select>
            <button onClick={() => refetch()} className="p-2 text-gray-400 hover:text-primary-600 border border-gray-200 rounded-lg" title="Refresh status">
              <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            {!creating && <button onClick={() => setCreating(true)} className="btn-primary text-sm whitespace-nowrap">New template</button>}
          </div>
        )}
      </div>

      {numbers.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          Connect a WhatsApp number first, in Settings → WhatsApp Integration.
        </div>
      ) : (
        <>
          {creating && <NewTemplateForm numberId={numberId} onDone={() => setCreating(false)} />}

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {tplLoading ? (
              <div className="p-6 text-center"><Spinner size="sm" /></div>
            ) : templates.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-400">No templates yet on this number's business account.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Name', 'Category', 'Language', 'Status', 'Message', ''].map(h => (
                        <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {templates.map(t => (
                      <tr key={t.id}>
                        <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{t.name}</td>
                        <td className="px-4 py-2 text-gray-600 capitalize">{String(t.category || '').toLowerCase()}</td>
                        <td className="px-4 py-2 text-gray-600">{t.language}</td>
                        <td className="px-4 py-2">
                          <span className={`badge ${STATUS_BADGE[t.status] || 'badge-secondary'}`}>{t.status}</span>
                          {t.status === 'REJECTED' && t.rejected_reason && t.rejected_reason !== 'NONE' && (
                            <p className="text-xs text-red-600 mt-1">{t.rejected_reason.replace(/_/g, ' ').toLowerCase()}</p>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-600 max-w-md"><p className="line-clamp-2">{bodyOf(t)}</p></td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => { if (window.confirm(`Delete template "${t.name}"? This removes it from WhatsApp for every number on this business account.`)) remove.mutate(t.name); }}
                            className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
